import React, { createContext, useContext, useState, useEffect } from 'react';
import { useFlashcardOperations } from './flashcardOperations';
import { useGroupOperations } from './groupOperations';
import { useScoreOperations } from './scoreOperations';
import { loadStudyStats, recordStudyActivity, saveStudyStats } from './studyStats';
import { buildDefaultStudyData } from './defaultSeed';

const FlashcardContext = createContext();

export function useFlashcards() {
  return useContext(FlashcardContext);
}

export function FlashcardProvider({ children }) {
  const defaultStudyData = buildDefaultStudyData();

  const [flashcards, setFlashcards] = useState(() => {
    const savedCards = localStorage.getItem('flashcards');
    if (savedCards) {
      return JSON.parse(savedCards);
    }
    return defaultStudyData.flashcards;
  });

  const [groups, setGroups] = useState(() => {
    const savedGroups = localStorage.getItem('groups');
    return savedGroups ? JSON.parse(savedGroups) : defaultStudyData.groups;
  });

  const [activeGroupId, setActiveGroupId] = useState(() => {
    const savedGroupId = localStorage.getItem('activeGroupId');
    return savedGroupId ? JSON.parse(savedGroupId) : defaultStudyData.activeGroupId;
  });

  const [score, setScore] = useState(() => {
    try {
      const savedScore = localStorage.getItem('score');
      if (savedScore) {
        const parsedScore = JSON.parse(savedScore);
        if (typeof parsedScore === 'number') {
          return {
            total: parsedScore,
            right: Math.max(0, parsedScore),
            wrong: Math.max(0, -parsedScore)
          };
        }
        return parsedScore;
      }
    } catch (e) {
      console.warn('Error loading score:', e);
    }
    return { total: 0, right: 0, wrong: 0 };
  });

  const [isMuted, setIsMuted] = useState(() => {
    const savedMuted = localStorage.getItem('isMuted');
    return savedMuted ? JSON.parse(savedMuted) : false;
  });

  const [studyStats, setStudyStats] = useState(loadStudyStats);
  const [studyMode, setStudyMode] = useState(() => {
    const savedMode = localStorage.getItem('studyMode');
    return savedMode ? JSON.parse(savedMode) : 'random';
  });

  useEffect(() => {
    localStorage.setItem('flashcards', JSON.stringify(flashcards));
  }, [flashcards]);

  useEffect(() => {
    localStorage.setItem('groups', JSON.stringify(groups));
  }, [groups]);

  useEffect(() => {
    localStorage.setItem('activeGroupId', JSON.stringify(activeGroupId));
  }, [activeGroupId]);

  useEffect(() => {
    localStorage.setItem('score', JSON.stringify(score));
  }, [score]);

  useEffect(() => {
    localStorage.setItem('isMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  useEffect(() => {
    saveStudyStats(studyStats);
  }, [studyStats]);

  useEffect(() => {
    localStorage.setItem('studyMode', JSON.stringify(studyMode));
  }, [studyMode]);

  const getVisibleFlashcards = () => {
    return flashcards.filter(card => card.groupId === activeGroupId);
  };

  const { 
    addFlashcard, 
    deleteFlashcard, 
    updateFlashcard,
    markCardAsReviewed,
    addFlashcardsToGroup
  } = useFlashcardOperations(setFlashcards, setGroups);

  const {
    createGroup,
    renameGroup,
    duplicateGroup,
    deleteGroup,
    sortedGroups
  } = useGroupOperations(groups, setGroups, flashcards, setFlashcards, activeGroupId, setActiveGroupId);

  const {
    updateScore,
    toggleMute
  } = useScoreOperations(
    setScore,
    setIsMuted,
    markCardAsReviewed,
    () => setStudyStats(prev => recordStudyActivity(prev))
  );

  const value = {
    flashcards: getVisibleFlashcards(),
    allFlashcards: flashcards,
    groups: sortedGroups,
    activeGroupId,
    addFlashcard,
    deleteFlashcard,
    updateFlashcard,
    createGroup,
    renameGroup,
    duplicateGroup,
    deleteGroup,
    addFlashcardsToGroup,
    setActiveGroupId,
    score,
    studyStats,
    studyMode,
    updateScore,
    isMuted,
    toggleMute,
    markCardAsReviewed,
    setStudyMode
  };

  return (
    <FlashcardContext.Provider value={value}>
      {children}
    </FlashcardContext.Provider>
  );
}
